import { organizationApi, OrganizationId } from "@/api/organizations";
import {
    projectConfigApi,
    type LabelConfiguration,
    type LabelConfigurationId,
    type PriorityConfiguration,
    type PriorityConfigurationId,
    type ProjectConfiguration,
    type ProjectConfigurationId,
    type TicketStatusConfiguration,
    type TicketStatusConfigurationId,
    type TicketTypeConfiguration,
    type TicketTypeConfigurationId,
} from "@/api/projectConfigs";
import type {
    ResolutionConfiguration,
    ResolutionConfigurationId,
} from "@/api/projectConfigs/resolutions";
import {
    projectApi,
    type Project,
    type ProjectId,
    type ProjectTeam,
    type ProjectUser,
    type ProjectVersion,
    type ProjectRoleHolders,
    type ProjectRoleHoldersEdit,
    ProjectPermission,
    type ProjectKey,
    type ProjectVersionId,
    type ProjectEdit,
    MilestoneId,
    MilestoneEdit,
    Milestone,
    ProjectVersionReleaseModel,
} from "@/api/projects";
import type { Team, TeamId } from "@/api/teams";
import { isActiveUser } from "@/api/users";
import { compareVersions, isDefined } from "@/utils";
import { AxiosError } from "axios";
import { defineStore } from "pinia";
import { LabelCreateModel } from "@/api/projectConfigs/labels";
import { ticketTypeApi, TicketTypeEdit } from "@/api/projectConfigs/types";
import {
    LabelId,
    PortalStage,
    PriorityId,
    ResolutionId,
    TicketResponsibilityRoleId,
    TicketStatusCategory,
    TicketStatusId,
    TicketStatusSetId,
    TicketStatusStageId,
    TicketTypeId,
} from "@/api/tickets";
import { PriorityEdit } from "@/api/projectConfigs/priorities";
import {
    TicketResponsibilityRule,
    TicketStatusDeletionResult,
    StatusSlaSettings,
    TicketStatusEdit,
    TicketStatusMigration,
    TicketStatusTransition,
} from "@/api/projectConfigs/statuses";
import { useAuthStore } from "./auth";
import { DynamicFormData, DynamicFormSchemaItemTypes, DynamicFormSelectItem } from "@/components";
import {
    CustomDataConfiguration,
    CustomDataConfigurationId,
    CustomDataFieldId,
    CustomDataPortalVisibility,
    CustomDataRule,
} from "@/api/projectConfigs/customData";
import {
    RoleConfiguration,
    RoleConfigurationId,
    ProjectRoleId,
    TicketStatusRoute,
} from "@/api/projectConfigs/roles";
import { TICKET_SEARCH_KEYS } from "@/queries/ticketSearch";
import { TICKET_LOOKUP_KEYS } from "@/queries/tickets";
import { readCachedUser, readCachedUsers } from "@/queries/users";
import {
    PROJECT_KEYS,
    customDataConfigsQueryOptions,
    ensureProjects,
    labelConfigsQueryOptions,
    priorityConfigsQueryOptions,
    projectConfigsQueryOptions,
    projectMilestonesQueryOptions,
    projectTeamsQueryOptions,
    projectUsersQueryOptions,
    projectVersionsQueryOptions,
    readCachedConfigList,
    readCachedProjectMilestones,
    readCachedProjectRoleHolders,
    readCachedProjectTeams,
    readCachedProjectUsers,
    readCachedProjectVersions,
    readCachedProjects,
    resolutionConfigsQueryOptions,
    roleConfigsQueryOptions,
    projectRoleHoldersQueryOptions,
    statusConfigsQueryOptions,
    typeConfigsQueryOptions,
} from "@/queries/projects";
import { ensureQueryData } from "@/queries/utils";
import { useQueryCache } from "@pinia/colada";

export type SubConfigDynamicFormData = {
    id: string | null;
    name: string;
} & DynamicFormData;

export type SubConfigDataModel = {
    id: string;
    name: string;
    items: SubConfigDynamicFormData[];
};

export type StatusFlow = {
    strictTransitions: boolean;
    transitions: TicketStatusTransition[];
};

function isProjectId(id: ProjectId | ProjectKey): id is ProjectId {
    return !!id.match(/[0-9]/);
}

function toMapById<TId extends string, T extends { id: TId }>(
    values: T[] | undefined
): Map<TId, T> | undefined {
    return values ? new Map(values.map((v) => [v.id, v])) : undefined;
}

// All project data lives in the query cache; getters read it reactively, load
// actions ensure it with stale-while-revalidate, and mutations refetch the
// keys they touched so the server's response is what every consumer sees.
export const useProjectsStore = defineStore("projects", {
    getters: {
        projects(): Map<ProjectId, Project> | undefined {
            return toMapById(readCachedProjects());
        },
        projectKeys(): Map<ProjectKey, Project> | undefined {
            const projects = readCachedProjects();
            return projects ? new Map(projects.map((p) => [p.key, p])) : undefined;
        },
        users(): Map<ProjectId, ProjectUser[]> {
            return readCachedProjectUsers();
        },
        teams(): Map<ProjectId, ProjectTeam[]> {
            return readCachedProjectTeams();
        },
        versions(): Map<ProjectId, ProjectVersion[]> {
            return readCachedProjectVersions();
        },
        milestones(): Map<ProjectId, Milestone[]> {
            return readCachedProjectMilestones();
        },
        configs(): Map<ProjectConfigurationId, ProjectConfiguration> | undefined {
            return toMapById(readCachedConfigList<ProjectConfiguration>(PROJECT_KEYS.configs()));
        },
        labelConfigs(): Map<LabelConfigurationId, LabelConfiguration> | undefined {
            return toMapById(readCachedConfigList<LabelConfiguration>(PROJECT_KEYS.labelConfigs()));
        },
        priorityConfigs(): Map<PriorityConfigurationId, PriorityConfiguration> | undefined {
            return toMapById(
                readCachedConfigList<PriorityConfiguration>(PROJECT_KEYS.priorityConfigs())
            );
        },
        resolutionConfigs(): Map<ResolutionConfigurationId, ResolutionConfiguration> | undefined {
            return toMapById(
                readCachedConfigList<ResolutionConfiguration>(PROJECT_KEYS.resolutionConfigs())
            );
        },
        statusConfigs(): Map<TicketStatusConfigurationId, TicketStatusConfiguration> | undefined {
            return toMapById(
                readCachedConfigList<TicketStatusConfiguration>(PROJECT_KEYS.statusConfigs())
            );
        },
        typeConfigs(): Map<TicketTypeConfigurationId, TicketTypeConfiguration> | undefined {
            return toMapById(
                readCachedConfigList<TicketTypeConfiguration>(PROJECT_KEYS.typeConfigs())
            );
        },
        customDataConfigs(): Map<CustomDataConfigurationId, CustomDataConfiguration> | undefined {
            return toMapById(
                readCachedConfigList<CustomDataConfiguration>(PROJECT_KEYS.customDataConfigs())
            );
        },
        roleConfigs(): Map<RoleConfigurationId, RoleConfiguration> | undefined {
            return toMapById(readCachedConfigList<RoleConfiguration>(PROJECT_KEYS.roleConfigs()));
        },
        roleHolders(): Map<ProjectId, ProjectRoleHolders[]> {
            return readCachedProjectRoleHolders();
        },
        get() {
            return (id: ProjectId | ProjectKey) =>
                isProjectId(id) ? this.projects?.get(id) : this.projectKeys?.get(id);
        },
        getProjects() {
            const authStore = useAuthStore();
            return (permission = ProjectPermission.CanViewProject) =>
                this.projects
                    ? Array.from(this.projects.values()).filter((p) =>
                          authStore.hasPermission(p.id, permission)
                      )
                    : undefined;
        },
        getOrganizationProjects() {
            return (id: OrganizationId) =>
                this.projects
                    ? Array.from(this.projects.values()).filter((p) => p.organizationId === id)
                    : undefined;
        },
        getUsers() {
            return (projectId: ProjectId) =>
                readCachedUsers()?.filter((u) =>
                    this.users.get(projectId)?.some((pu) => pu.id === u.id)
                );
        },
        getUsersWithPermission() {
            return (projectId: ProjectId, permission = ProjectPermission.CanViewProject) =>
                (this.users.get(projectId) ?? [])
                    .filter((pu) =>
                        pu.permissions.some(
                            (p) => p === permission || p === ProjectPermission.ProjectAdministrator
                        )
                    )
                    .map((pu) => readCachedUser(pu.id))
                    .filter(isDefined)
                    .filter(isActiveUser);
        },
        getTeam() {
            return (projectId: ProjectId, teamId: TeamId) =>
                this.teams.get(projectId)?.find((t) => t.teamId === teamId) ?? null;
        },
        getPriorities() {
            return (id: ProjectId) => {
                const configId = this.get(id)?.configurationId;
                if (!configId) {
                    return [];
                }
                const priorityConfigId = this.configs?.get(configId)?.priorityConfigurationId;
                if (!priorityConfigId) {
                    return [];
                }
                return this.priorityConfigs?.get(priorityConfigId)?.priorities || [];
            };
        },
        getResolutions() {
            return (id: ProjectId) => {
                const configId = this.get(id)?.configurationId;
                if (!configId) {
                    return [];
                }
                const resolutionConfigId = this.configs?.get(configId)?.resolutionConfigurationId;
                if (!resolutionConfigId) {
                    return [];
                }
                return this.resolutionConfigs?.get(resolutionConfigId)?.resolutions || [];
            };
        },
        getStatuses() {
            return (id: ProjectId) => {
                const configId = this.get(id)?.configurationId;
                if (!configId) {
                    return [];
                }
                const statusConfigId = this.configs?.get(configId)?.ticketStatusConfigurationId;
                if (!statusConfigId) {
                    return [];
                }
                const statuses = this.statusConfigs?.get(statusConfigId)?.statuses || [];
                return [...statuses].sort(
                    (a, b) => a.order - b.order || a.name.localeCompare(b.name)
                );
            };
        },
        getStatusFlow() {
            return (id: ProjectId): StatusFlow => {
                const configId = this.get(id)?.configurationId;
                const statusConfigId = configId
                    ? this.configs?.get(configId)?.ticketStatusConfigurationId
                    : undefined;
                const statusConfig = statusConfigId
                    ? this.statusConfigs?.get(statusConfigId)
                    : undefined;

                return {
                    strictTransitions: statusConfig?.strictTransitions ?? false,
                    transitions: statusConfig?.transitions ?? [],
                };
            };
        },
        getTypes() {
            return (id: ProjectId) => {
                const configId = this.get(id)?.configurationId;
                if (!configId) {
                    return [];
                }
                const typeConfigId = this.configs?.get(configId)?.ticketTypeConfigurationId;
                if (!typeConfigId) {
                    return [];
                }
                return this.typeConfigs?.get(typeConfigId)?.types || [];
            };
        },
        // Statuses a type may use in this project; null = unrestricted.
        getAllowedStatusIds() {
            return (id: ProjectId, typeId: TicketTypeId): Set<TicketStatusId> | null => {
                const configId = this.get(id)?.configurationId;
                const statusConfigId = configId
                    ? this.configs?.get(configId)?.ticketStatusConfigurationId
                    : undefined;
                const statusConfig = statusConfigId
                    ? this.statusConfigs?.get(statusConfigId)
                    : undefined;
                const type = this.getTypes(id).find((t) => t.id === typeId);
                if (!statusConfig || !type) {
                    return null;
                }

                const sets = statusConfig.statusSets.filter((s) =>
                    type.statusSetIds.includes(s.id)
                );
                if (sets.length === 0) {
                    return null;
                }

                return new Set(sets.flatMap((s) => s.statusIds));
            };
        },
        getVersions() {
            return (id: ProjectId) => (this.versions.get(id) || []).toSorted(compareVersions);
        },
        getMilestones() {
            return (id: ProjectId) => this.milestones.get(id) || [];
        },
        getProjectConfiguration() {
            return (id: ProjectId) => {
                const configId = this.get(id)?.configurationId;
                if (!configId) return undefined;
                return this.configs?.get(configId);
            };
        },
        getProjectLabelConfiguration() {
            return (id: ProjectId) => {
                const config = this.getProjectConfiguration(id);
                if (!config) return undefined;
                return this.labelConfigs?.get(config.labelConfigurationId);
            };
        },
        getLabels() {
            return (id: ProjectId) => {
                const configId = this.get(id)?.configurationId;
                if (!configId) {
                    return [];
                }
                const labelConfigId = this.configs?.get(configId)?.labelConfigurationId;
                if (!labelConfigId) {
                    return [];
                }
                return this.labelConfigs?.get(labelConfigId)?.labels || [];
            };
        },
    },
    actions: {
        async load(force = false) {
            await Promise.all([this.loadProjects(force), this.loadConfigs(force)]);
        },

        async loadProjects(force = false) {
            await ensureProjects(force);
        },
        setProject(project: Project) {
            useQueryCache().setQueryData<Project[]>(PROJECT_KEYS.list(), (projects) => [
                ...(projects ?? []).filter((p) => p.id !== project.id),
                project,
            ]);
        },
        async createProject(
            id: OrganizationId,
            name: string,
            key: string,
            configuration: ProjectConfigurationId
        ) {
            const project = await organizationApi.createProject(id, name, key, configuration);
            this.setProject(project);
            return project;
        },

        async editProject(id: ProjectId, model: ProjectEdit) {
            await projectApi.editProject(id, model);

            useQueryCache().setQueryData<Project[]>(PROJECT_KEYS.list(), (projects) =>
                (projects ?? []).map((p) => (p.id === id ? { ...p, name: model.name } : p))
            );
        },

        async deleteProject(id: ProjectId) {
            try {
                await projectApi.deleteProject(id);
            } catch (ex) {
                if (ex instanceof AxiosError) {
                    return ex.response?.data as string;
                }

                throw ex;
            }

            useQueryCache().setQueryData<Project[]>(PROJECT_KEYS.list(), (projects) =>
                (projects ?? []).filter((p) => p.id !== id)
            );

            return;
        },

        async changeOrganization(id: ProjectId, organization: OrganizationId) {
            await projectApi.changeOrganization(id, organization);

            useQueryCache().setQueryData<Project[]>(PROJECT_KEYS.list(), (projects) =>
                (projects ?? []).map((p) =>
                    p.id === id ? { ...p, organizationId: organization } : p
                )
            );
        },

        async loadProject(id: ProjectId, force = false) {
            if (force || !this.projects?.has(id)) {
                await ensureProjects(true);
            }

            await this.loadUsers(id, force);
            await this.loadTeams(id, force);
            await this.loadVersions(id, force);
            await this.loadMilestones(id, force);
        },
        async loadUsers(id: ProjectId, force = false) {
            await ensureQueryData(projectUsersQueryOptions(id), force);
        },
        async loadTeams(id: ProjectId, force = false) {
            await ensureQueryData(projectTeamsQueryOptions(id), force);
        },
        async addTeam(project: ProjectId, team: Team) {
            await projectApi.addTeam(project, team.id);

            await this.loadTeams(project, true);
            await this.loadUsers(project, true);
        },
        async removeTeam(project: ProjectId, team: Team) {
            await projectApi.removeTeam(project, team.id);

            await this.loadTeams(project, true);
            await this.loadUsers(project, true);
        },

        async loadVersions(id: ProjectId, force = false) {
            await ensureQueryData(projectVersionsQueryOptions(id), force);
        },
        async createVersion(
            project: ProjectId,
            name: string,
            startDate?: Date,
            endDate?: Date,
            freezeDate?: Date
        ) {
            const version = await projectApi.createVersion(
                project,
                name,
                startDate,
                endDate,
                freezeDate
            );

            await this.loadVersions(project, true);

            return version;
        },
        async editVersion(
            project: ProjectId,
            version: ProjectVersion,
            name: string,
            startDate: string | null,
            endDate: string | null,
            freezeDate: string | null,
            releaseDate: string | null,
            theme: string | null,
            notes: string | null,
            hiddenFromRadar: boolean
        ) {
            await projectApi.editVersion(project, version, {
                name,
                startDate,
                endDate,
                freezeDate,
                releaseDate,
                theme,
                notes,
                hiddenFromRadar,
            });

            await this.loadVersions(project, true);
        },
        async setVersionHiddenFromRadar(
            project: ProjectId,
            version: ProjectVersionId,
            hidden: boolean
        ) {
            await projectApi.setVersionHiddenFromRadar(project, version, hidden);

            await this.loadVersions(project, true);
        },
        async releaseVersion(
            project: ProjectId,
            version: ProjectVersionId,
            model: ProjectVersionReleaseModel
        ) {
            await projectApi.releaseVersion(project, version, model);

            await this.loadVersions(project, true);
        },
        async deleteVersion(project: ProjectId, version: ProjectVersionId) {
            try {
                await projectApi.deleteVersion(project, version);
            } catch (ex) {
                if (ex instanceof AxiosError) {
                    return ex.response?.data as string;
                }
            }

            await this.loadVersions(project, true);

            return;
        },

        async loadMilestones(project: ProjectId, force = false) {
            await ensureQueryData(projectMilestonesQueryOptions(project), force);
        },

        async createMilestone(project: ProjectId, milestone: Omit<Milestone, "id">) {
            await projectApi.createMilestone(
                project,
                milestone.name,
                milestone.startDate == null ? undefined : milestone.startDate,
                milestone.endDate == null ? undefined : milestone.endDate
            );

            await this.loadMilestones(project, true);
        },

        async editMilestone(project: ProjectId, milestone: MilestoneId, patch: MilestoneEdit) {
            const m = this.milestones.get(project)?.find((mi) => mi.id == milestone);
            if (m != undefined) {
                await projectApi.editMilestone(project, m, patch);
            }
            await this.loadMilestones(project, true);
        },

        async deleteMilestone(project: ProjectId, milestone: MilestoneId) {
            await projectApi.deleteMilestone(project, milestone);
            await this.loadMilestones(project, true);
        },

        async setPermissions(
            projectId: ProjectId,
            teamId: TeamId,
            permissions: ProjectPermission[]
        ) {
            const team = this.teams.get(projectId)?.find((t) => t.teamId === teamId);
            if (!team) {
                return;
            }

            await projectApi.setTeamPermissions(projectId, teamId, permissions);

            await this.loadTeams(projectId, true);
            await this.loadUsers(projectId, true);
        },

        async createLabels(labelConfigId: LabelConfigurationId, labelNames: string[]) {
            const labelConfig = this.labelConfigs?.get(labelConfigId);
            if (!labelConfig) {
                return;
            }
            if (
                labelConfig.labels.map((lcl) => lcl.name).filter((n) => labelNames.includes(n))
                    .length > 0
            ) {
                return;
            }

            const model: LabelCreateModel = {
                names: labelNames.map((ln) => ({
                    name: ln,
                })),
            };
            const res = await projectConfigApi.createLabels(labelConfig.id, model);
            await this.loadLabelConfigs(true);
            return res;
        },

        async loadConfigs(force = false) {
            // Each configuration set comes from an independent endpoint; load
            // them in parallel instead of waterfalling seven requests.
            await Promise.all([
                this.loadProjectConfigs(force),
                this.loadLabelConfigs(force),
                this.loadPriorityConfigs(force),
                this.loadResolutionConfigs(force),
                this.loadStatusConfigs(force),
                this.loadTypeConfigs(force),
                this.loadCustomDataConfigs(force),
                this.loadRoleConfigs(force),
            ]);
        },
        async loadProjectConfigs(force = false) {
            await ensureQueryData(projectConfigsQueryOptions, force);
        },
        async createProjectConfig(
            name: string,
            labelConfigurationId: LabelConfigurationId,
            resolutionConfigurationId: ResolutionConfigurationId,
            ticketStatusConfigurationId: TicketStatusConfigurationId,
            ticketTypeConfigurationId: TicketTypeConfigurationId,
            priorityConfigurationId: PriorityConfigurationId
        ) {
            await projectConfigApi.createProjectConfiguration(
                name,
                labelConfigurationId,
                resolutionConfigurationId,
                ticketStatusConfigurationId,
                ticketTypeConfigurationId,
                priorityConfigurationId
            );

            await this.loadProjectConfigs(true);
        },
        async editProjectConfig(
            config: ProjectConfigurationId,
            patch: ProjectConfiguration,
            migrationModel: TicketStatusMigration[]
        ) {
            const currentConfig = this.configs?.get(config);
            if (currentConfig != undefined) {
                await projectConfigApi.editProjectConfiguration(
                    currentConfig,
                    patch,
                    migrationModel
                );
            }

            await this.loadProjectConfigs(true);

            if (migrationModel.length > 0) {
                await useQueryCache().invalidateQueries({
                    key: TICKET_LOOKUP_KEYS.statuses(),
                });
                await this.loadStatusConfigs(true);
                // Cached search rows may show migrated statuses; refetch them.
                await useQueryCache().invalidateQueries({ key: TICKET_SEARCH_KEYS.root });
            }
        },
        async deleteProjectConfig(config: ProjectConfigurationId) {
            const projectConfig = this.configs?.get(config);

            if (projectConfig) {
                await projectConfigApi.deleteProjectConfiguration(config);

                await this.loadProjectConfigs(true);
            }
        },
        async loadLabelConfigs(force = false) {
            await ensureQueryData(labelConfigsQueryOptions, force);
        },
        async createLabelConfig(name: string) {
            const config = await projectConfigApi.createLabelConfiguration(name);
            await this.loadLabelConfigs(true);
            return config;
        },
        async editLabelConfig(config: LabelConfigurationId, name: string) {
            await projectConfigApi.editLabelConfiguration(config, name);
            await this.loadLabelConfigs(true);
        },
        async deleteLabelConfig(config: LabelConfigurationId) {
            await projectConfigApi.deleteLabelConfiguration(config);
            await this.loadLabelConfigs(true);
        },
        async editLabel(config: LabelConfigurationId, label: LabelId, name: string) {
            await projectConfigApi.editLabel(config, label, name);
            await this.loadLabelConfigs(true);
        },
        async deleteLabel(config: LabelConfigurationId, label: LabelId) {
            await projectConfigApi.deleteLabel(config, label);
            await this.loadLabelConfigs(true);
        },
        async loadPriorityConfigs(force = false) {
            await ensureQueryData(priorityConfigsQueryOptions, force);
        },
        async createPriorityConfig(name: string) {
            const config = await projectConfigApi.createPriorityConfiguration(name);
            await this.loadPriorityConfigs(true);
            return config;
        },
        async editPriorityConfig(config: PriorityConfigurationId, name: string) {
            await projectConfigApi.editPriorityConfiguration(config, name);
            await this.loadPriorityConfigs(true);
        },
        async deletePriorityConfig(config: PriorityConfigurationId) {
            await projectConfigApi.deletePriorityConfiguration(config);
            await this.loadPriorityConfigs(true);
        },
        async createPriority(
            config: PriorityConfigurationId,
            name: string,
            order: number,
            icon: string,
            iconColor: string,
            description: string | null
        ) {
            await projectConfigApi.createPriority(
                config,
                name,
                order,
                icon,
                iconColor,
                description
            );
            await this.loadPriorityConfigs(true);
        },
        async editPriority(
            config: PriorityConfigurationId,
            priority: PriorityId,
            edit: PriorityEdit
        ) {
            await projectConfigApi.editPriority(config, priority, edit);
            await this.loadPriorityConfigs(true);
        },
        async patchPriority(
            config: PriorityConfigurationId,
            priority: PriorityId,
            patch: Partial<PriorityEdit>
        ) {
            const currentPriority = this.priorityConfigs
                ?.get(config)
                ?.priorities.find((p) => p.id === priority);
            if (!currentPriority) {
                return;
            }

            await projectConfigApi.patchPriority(config, currentPriority, patch);
            await this.loadPriorityConfigs(true);
        },
        async deletePriority(config: PriorityConfigurationId, priority: PriorityId) {
            await projectConfigApi.deletePriority(config, priority);
            await this.loadPriorityConfigs(true);
        },
        async loadResolutionConfigs(force = false) {
            await ensureQueryData(resolutionConfigsQueryOptions, force);
        },
        async createResolutionConfig(name: string) {
            const config = await projectConfigApi.createResolutionConfiguration(name);
            await this.loadResolutionConfigs(true);
            return config;
        },
        async editResolutionConfig(config: ResolutionConfigurationId, name: string) {
            await projectConfigApi.editResolutionConfiguration(config, name);
            await this.loadResolutionConfigs(true);
        },
        async deleteResolutionConfig(config: ResolutionConfigurationId) {
            await projectConfigApi.deleteResolutionConfiguration(config);
            await this.loadResolutionConfigs(true);
        },
        async createResolution(
            config: ResolutionConfigurationId,
            name: string,
            description: string | null
        ) {
            await projectConfigApi.createResolution(config, name, description);
            await this.loadResolutionConfigs(true);
        },
        async editResolution(
            config: ResolutionConfigurationId,
            resolution: ResolutionId,
            name: string,
            description: string | null
        ) {
            await projectConfigApi.editResolution(config, resolution, name, description);
            await this.loadResolutionConfigs(true);
        },
        async deleteResolution(config: ResolutionConfigurationId, resolution: ResolutionId) {
            await projectConfigApi.deleteResolution(config, resolution);
            await this.loadResolutionConfigs(true);
        },
        async loadRoleConfigs(force = false) {
            await ensureQueryData(roleConfigsQueryOptions, force);
        },
        async createRoleConfig(name: string) {
            const config = await projectConfigApi.createRoleConfiguration(name);
            await this.loadRoleConfigs(true);
            return config;
        },
        async editRoleConfig(config: RoleConfigurationId, name: string) {
            await projectConfigApi.editRoleConfiguration(config, name);
            await this.loadRoleConfigs(true);
        },
        async deleteRoleConfig(config: RoleConfigurationId) {
            await projectConfigApi.deleteRoleConfiguration(config);
            await this.loadRoleConfigs(true);
        },
        async createRole(config: RoleConfigurationId, name: string) {
            await projectConfigApi.createRole(config, name);
            await this.loadRoleConfigs(true);
        },
        async editRole(config: RoleConfigurationId, role: ProjectRoleId, name: string) {
            await projectConfigApi.editRole(config, role, name);
            await this.loadRoleConfigs(true);
        },
        async reorderRoles(config: RoleConfigurationId, roleIds: ProjectRoleId[]) {
            await projectConfigApi.reorderRoles(config, roleIds);
            await this.loadRoleConfigs(true);
        },
        async deleteRole(config: RoleConfigurationId, role: ProjectRoleId) {
            await projectConfigApi.deleteRole(config, role);
            await this.loadRoleConfigs(true);
        },
        async setStatusRoutes(config: ProjectConfigurationId, routes: TicketStatusRoute[]) {
            await projectConfigApi.setStatusRoutes(config, routes);
            await this.loadProjectConfigs(true);
        },
        async loadRoleHolders(id: ProjectId, force = false) {
            await ensureQueryData(projectRoleHoldersQueryOptions(id), force);
        },
        async setRoleHolders(id: ProjectId, holders: ProjectRoleHoldersEdit[]) {
            const result = await projectApi.setRoleHolders(id, holders);
            useQueryCache().setQueryData(PROJECT_KEYS.roleHolders(id), result);
        },
        async loadStatusConfigs(force = false) {
            await ensureQueryData(statusConfigsQueryOptions, force);
        },
        async createStatusConfig(name: string) {
            const config = await projectConfigApi.createStatusConfiguration(name);
            await this.loadStatusConfigs(true);
            return config;
        },
        async editStatusConfig(config: TicketStatusConfigurationId, name: string) {
            await projectConfigApi.editStatusConfiguration(config, name);
            await this.loadStatusConfigs(true);
        },
        async editStatusFlow(
            config: TicketStatusConfigurationId,
            strictTransitions: boolean,
            transitions: TicketStatusTransition[],
            entryStatusIds: TicketStatusId[]
        ) {
            await projectConfigApi.editStatusFlow(
                config,
                strictTransitions,
                transitions,
                entryStatusIds
            );
            await this.loadStatusConfigs(true);
        },
        async createStatusSet(
            config: TicketStatusConfigurationId,
            name: string,
            statusIds: TicketStatusId[]
        ) {
            const set = await projectConfigApi.createStatusSet(config, name, statusIds);
            await this.loadStatusConfigs(true);
            return set;
        },
        async editStatusSet(
            config: TicketStatusConfigurationId,
            setId: TicketStatusSetId,
            name: string,
            statusIds: TicketStatusId[]
        ) {
            await projectConfigApi.editStatusSet(config, setId, name, statusIds);
            await this.loadStatusConfigs(true);
        },
        async createStatusStage(
            config: TicketStatusConfigurationId,
            name: string,
            color: string | null
        ) {
            const stage = await projectConfigApi.createStatusStage(config, name, color);
            await this.loadStatusConfigs(true);
            return stage;
        },
        async editStatusStage(
            config: TicketStatusConfigurationId,
            stageId: TicketStatusStageId,
            name: string,
            color: string | null
        ) {
            await projectConfigApi.editStatusStage(config, stageId, name, color);
            await this.loadStatusConfigs(true);
        },
        async reorderStatusStages(
            config: TicketStatusConfigurationId,
            stageIds: TicketStatusStageId[]
        ) {
            await projectConfigApi.reorderStatusStages(config, stageIds);
            await this.loadStatusConfigs(true);
        },
        async deleteStatusStage(config: TicketStatusConfigurationId, stageId: TicketStatusStageId) {
            await projectConfigApi.deleteStatusStage(config, stageId);
            await this.loadStatusConfigs(true);
        },
        async deleteStatusSet(config: TicketStatusConfigurationId, setId: TicketStatusSetId) {
            await projectConfigApi.deleteStatusSet(config, setId);
            // Types referencing the set lose it server-side too.
            await this.loadStatusConfigs(true);
            await this.loadTypeConfigs(true);
        },
        async createResponsibilityRole(config: TicketStatusConfigurationId, name: string) {
            const role = await projectConfigApi.createResponsibilityRole(config, name);
            await this.loadStatusConfigs(true);
            return role;
        },
        async editResponsibilityRole(
            config: TicketStatusConfigurationId,
            roleId: TicketResponsibilityRoleId,
            name: string
        ) {
            await projectConfigApi.editResponsibilityRole(config, roleId, name);
            await this.loadStatusConfigs(true);
        },
        async setResponsibilityRules(
            config: TicketStatusConfigurationId,
            roleId: TicketResponsibilityRoleId,
            rules: TicketResponsibilityRule[]
        ) {
            await projectConfigApi.setResponsibilityRules(config, roleId, rules);
            await this.loadStatusConfigs(true);
        },
        async deleteResponsibilityRole(
            config: TicketStatusConfigurationId,
            roleId: TicketResponsibilityRoleId
        ) {
            await projectConfigApi.deleteResponsibilityRole(config, roleId);
            await this.loadStatusConfigs(true);
        },
        async deleteStatusConfig(config: TicketStatusConfigurationId) {
            await projectConfigApi.deleteStatusConfiguration(config);
            await this.loadStatusConfigs(true);
        },
        async createStatus(
            config: TicketStatusConfigurationId,
            name: string,
            category: TicketStatusCategory,
            color: string | null,
            description: string | null,
            portalStage: PortalStage | null,
            stageId: TicketStatusStageId | null,
            sla: StatusSlaSettings
        ) {
            const newStatus = await projectConfigApi.createStatus(
                config,
                name,
                category,
                color,
                description,
                portalStage,
                stageId,
                sla
            );
            await this.loadStatusConfigs(true);

            return newStatus;
        },
        async editStatus(
            config: TicketStatusConfigurationId,
            statusId: TicketStatusId,
            name: string,
            category: TicketStatusCategory,
            color: string | null,
            description: string | null,
            portalStage: PortalStage | null,
            stageId: TicketStatusStageId | null,
            sla: StatusSlaSettings
        ) {
            await projectConfigApi.editStatus(
                config,
                statusId,
                name,
                category,
                color,
                description,
                portalStage,
                stageId,
                sla
            );
            await this.loadStatusConfigs(true);
        },
        async patchStatus(
            config: TicketStatusConfigurationId,
            status: TicketStatusId,
            patch: Partial<TicketStatusEdit>
        ) {
            const currentStatus = this.statusConfigs
                ?.get(config)
                ?.statuses.find((s) => s.id === status);
            if (!currentStatus) {
                return;
            }

            await projectConfigApi.patchStatus(config, currentStatus, patch);
            await this.loadStatusConfigs(true);
        },

        async reorderStatuses(config: TicketStatusConfigurationId, statusIds: TicketStatusId[]) {
            await projectConfigApi.reorderStatuses(config, statusIds);
            await this.loadStatusConfigs(true);
        },
        async migrateStatuses(
            configId: TicketStatusConfigurationId,
            migrations: TicketStatusMigration[]
        ) {
            const success = await projectConfigApi.migrateStatuses(configId, migrations);
            if (success) {
                await useQueryCache().invalidateQueries({
                    key: TICKET_LOOKUP_KEYS.statuses(),
                });
                await this.loadStatusConfigs(true);
                // Cached search rows may show migrated statuses; refetch them.
                await useQueryCache().invalidateQueries({ key: TICKET_SEARCH_KEYS.root });
            }
        },
        async deleteStatus(
            config: TicketStatusConfigurationId,
            status: TicketStatusId,
            migrations: TicketStatusMigration[]
        ): Promise<TicketStatusDeletionResult | undefined> {
            const currentStatus = this.statusConfigs
                ?.get(config)
                ?.statuses.find((s) => s.id === status);
            if (!currentStatus) {
                return;
            }

            const result = await projectConfigApi.deleteStatus(config, status, migrations);
            await this.loadStatusConfigs(true);
            return result;
        },
        async loadTypeConfigs(force = false) {
            await ensureQueryData(typeConfigsQueryOptions, force);
        },
        async createTypeConfig(name: string) {
            const config = await ticketTypeApi.createTypeConfiguration(name);
            await this.loadTypeConfigs(true);
            return config;
        },
        async editTypeConfig(config: TicketTypeConfigurationId, name: string) {
            await ticketTypeApi.editTypeConfiguration(config, name);
            await this.loadTypeConfigs(true);
        },
        async deleteTypeConfig(config: TicketTypeConfigurationId) {
            await ticketTypeApi.deleteTypeConfiguration(config);
            await this.loadTypeConfigs(true);
        },
        async createType(
            config: TicketTypeConfigurationId,
            name: string,
            icon: string | null,
            iconColor: string | null,
            description: string | null,
            subtaskOnly: boolean,
            statusSetIds: TicketStatusSetId[]
        ) {
            await ticketTypeApi.createType(
                config,
                name,
                icon,
                iconColor,
                description,
                subtaskOnly,
                statusSetIds
            );
            await this.loadTypeConfigs(true);
        },
        async editType(
            config: TicketTypeConfigurationId,
            type: TicketTypeId,
            name: string,
            icon: string | null,
            iconColor: string | null,
            description: string | null,
            subtaskOnly: boolean,
            statusSetIds: TicketStatusSetId[]
        ) {
            await ticketTypeApi.editType(
                config,
                type,
                name,
                icon,
                iconColor,
                description,
                subtaskOnly,
                statusSetIds
            );
            await this.loadTypeConfigs(true);
        },
        async patchType(
            config: TicketTypeConfigurationId,
            type: TicketTypeId,
            patch: Partial<TicketTypeEdit>
        ) {
            const ticketType = this.typeConfigs?.get(config)?.types.find((t) => t.id === type);
            if (!ticketType) {
                return;
            }

            await projectConfigApi.patchType(config, ticketType, patch);
            await this.loadTypeConfigs(true);
        },
        async deleteType(config: TicketTypeConfigurationId, type: TicketTypeId) {
            await ticketTypeApi.deleteType(config, type);
            await this.loadTypeConfigs(true);
        },

        async loadCustomDataConfigs(force: boolean = false) {
            await ensureQueryData(customDataConfigsQueryOptions, force);
        },
        async createCustomDataConfig(name: string) {
            const config = await projectConfigApi.createCustomDataConfiguration(name);
            await this.loadCustomDataConfigs(true);
            return config;
        },
        async editCustomDataConfig(config: CustomDataConfigurationId, name: string) {
            await projectConfigApi.editCustomDataConfiguration(config, name);
            await this.loadCustomDataConfigs(true);
        },
        async deleteCustomDataConfig(config: CustomDataConfigurationId) {
            await projectConfigApi.deleteCustomDataConfiguration(config);
            await this.loadCustomDataConfigs(true);
        },
        async createCustomDataField(
            config: CustomDataConfigurationId,
            name: string,
            fieldName: string,
            type: DynamicFormSchemaItemTypes,
            options?: DynamicFormSelectItem[],
            rules?: CustomDataRule[],
            portalVisibility?: CustomDataPortalVisibility
        ) {
            const result = await projectConfigApi.createCustomDataField(
                config,
                name,
                fieldName,
                type,
                options,
                rules,
                portalVisibility
            );
            await this.loadCustomDataConfigs(true);
            return result;
        },
        async editCustomDataField(
            config: CustomDataConfigurationId,
            field: CustomDataFieldId,
            name?: string,
            fieldName?: string,
            type?: DynamicFormSchemaItemTypes,
            options?: DynamicFormSelectItem[],
            rules?: CustomDataRule[],
            portalVisibility?: CustomDataPortalVisibility
        ) {
            await projectConfigApi.editCustomDataField(
                config,
                field,
                name,
                fieldName,
                type,
                options,
                rules,
                portalVisibility
            );
            await this.loadCustomDataConfigs(true);
        },
        async patchCustomDataField(
            config: CustomDataConfigurationId,
            field: CustomDataFieldId,
            name?: string,
            fieldName?: string,
            type?: DynamicFormSchemaItemTypes,
            options?: DynamicFormSelectItem[],
            rules?: CustomDataRule[]
        ) {
            await this.editCustomDataField(config, field, name, fieldName, type, options, rules);
        },
        async reorderCustomDataFields(
            config: CustomDataConfigurationId,
            fieldIds: CustomDataFieldId[]
        ) {
            await projectConfigApi.reorderCustomDataFields(config, fieldIds);
            await this.loadCustomDataConfigs(true);
        },
        async deleteCustomDataField(
            config: CustomDataConfigurationId,
            field: CustomDataFieldId,
            clearValues = false
        ) {
            await projectConfigApi.deleteCustomDataField(config, field, clearValues);
            await this.loadCustomDataConfigs(true);
        },
    },
});
